import { useScenarioAndAssociationsQuery } from '@api/scenarios/queries/useScenarioAndAssociationsQuery'
import { useNotesQuery } from './useNotesQuery'
import { jointScenarioQueryKeys } from '../queryKeys'
import { scenarioQueryKeys } from '../../scenarios/queryKeys'
import { useForecastQuery } from './useForecastQuery'
import { useAssetAllocationQuery } from './useAssetAllocationQuery'
import { useJointBudgetQuery } from './useJointBudgetQuery'
import { useJointCashflowEventsQuery } from './useJointCashflowEventsQuery'

// FIX: joint-planning-fix: why does one of the forecast query always hang when 
// updating something on the joint plan
export function useJointScenarioAndAssociationsQuery({ jointScenarioId, jointScenarioData }) {
  const participants = jointScenarioData?.participants || []
  const scenarioIds = participants.map((participant) => participant.scenario.baseScenario.id)

  const participantsQueries = participants.map((participant) => {
    const participantQuery = useScenarioAndAssociationsQuery({
      scenarioID: participant.scenario.baseScenario.id,
      defaultValues: participant,
      additionalInvalidationKeys: [
        jointScenarioQueryKeys.forecast,
        jointScenarioQueryKeys.assetAllocation
      ]
    })
    return {
      ...participantQuery,
      participant: {
        id: participant.id,
        name: participant.name,
        preferredName: participant.preferredName,
        currentAge: participant.currentAge,
        expectedAge: participant.expectedAge,
        scenarioId: participant.scenario.baseScenario.id,
      }
    }
  })

  const notes = useNotesQuery({
    jointScenarioId,
    initialData: jointScenarioData.notes,
    queryKey: [jointScenarioQueryKeys.notes]
  })

  const assetAllocation = useAssetAllocationQuery({
    jointScenarioId,
    initialData: jointScenarioData.assetAllocation,
    queryKey: [jointScenarioQueryKeys.assetAllocation]
  })

  const forecast = useForecastQuery({
    jointScenarioId,
    initialData: jointScenarioData.jointScenario.forecast,
    queryKey: [jointScenarioQueryKeys.forecast]
  })

  const jointBudget = useJointBudgetQuery({
    jointScenarioId,
    initialData: jointScenarioData.jointBudget,
    queryKey: [jointScenarioQueryKeys.jointBudget],
    additionalInvalidationKeys: [
      ...scenarioIds.map((scenarioId) => scenarioQueryKeys(scenarioId).budget),
      ...scenarioIds.map((scenarioId) => scenarioQueryKeys(scenarioId).forecasts),
    ]
  })

  const jointCashflowEvents = useJointCashflowEventsQuery({
    jointScenarioId,
    initialData: jointScenarioData.jointCashflowEvents,
    queryKey: [jointScenarioQueryKeys.jointCashflowEvents],
    additionalInvalidationKeys: [
      ...scenarioIds.map((scenarioId) => scenarioQueryKeys(scenarioId).forecasts),
      ...scenarioIds.map((scenarioId) => scenarioQueryKeys(scenarioId).cashflowEvents)
    ]
  })


  const isFetching = participantsQueries.some((query) => query.isFetching) ||
    notes.isFetching ||
    jointCashflowEvents.isFetching ||
    forecast.isFetching

  const loadingIndicators = {
    assets: participantsQueries.some((query) => query.loadingIndicators.assets),
    cashflowEvents: participantsQueries.some((query) => query.loadingIndicators.cashflowEvents),
    budget: participantsQueries.some((query) => query.loadingIndicators.budget),
    liabilities: participantsQueries.some((query) => query.loadingIndicators.liabilities),
    riskProfile: participantsQueries.some((query) => query.loadingIndicators.riskProfile),
    riskProducts: participantsQueries.some((query) => query.loadingIndicators.riskProducts),
    fna: participantsQueries.some((query) => query.loadingIndicators.fna),
    scenario: participantsQueries.some((query) => query.loadingIndicators.scenario),
    forecast: forecast.isAnyLoading,
    notes: notes.isAnyLoading,
    jointCashflowEvents: jointCashflowEvents.isAnyLoading,
    jointBudget: jointBudget.isAnyLoading,
    assetAllocation: assetAllocation.isAnyLoading,
  }

  return {
    participantsQueries,

    notes,
    forecast,
    assetAllocation,
    jointBudget,
    jointCashflowEvents,
    isFetching,
    loadingIndicators
  }
}
